// src/components/WeeklyTask.jsx
import React, { useState, useEffect } from "react";
import { Card, CardBody, CardTitle, Col, Row, Progress } from "reactstrap";
import api from "../../api";         // your axios instance
import { parseISO, addDays, differenceInSeconds } from "date-fns";

export default function WeeklyTask() {
  const [weekStart, setWeekStart] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [timeLeft, setTimeLeft] = useState("");

  const goalByKey = {
    catch_pokemon100:       100,
    catch_shinies5_any:      5,
    catch_shinies5_specific: 5,
    pokedex_new:             1,
    beat_train5:             5,
    beat_wild25:            25,
    shard5_any:              5,
    shard5_spec:             5,
    use_mining_potion:       1,
    use_drop_potion:         1,
    use_ultra_lure2:         2,
    sell_pokemon100:        100,
    collect_gold10:         10,
    collect_gems15:         15,
    collect_diamonds5:       5,
    collect_rocks50:        50
  };

  // 1) Fetch weekly data when mounted
  useEffect(() => {
    api.get("/v1/weekly-reward-details", { withCredentials: true })
      .then(res => {
        const { week_start, challenges } = res.data;
        setWeekStart(week_start);
        setChallenges(challenges);
      })
      .catch(console.error);
  }, []);

  // 2) Live countdown to week_start + 7 days
  useEffect(() => {
    if (!weekStart) return;
    const deadline = addDays(parseISO(weekStart), 7);

    const tick = () => {
      const sec = differenceInSeconds(deadline, new Date());
      if (sec <= 0) {
        setTimeLeft("Expired");
        clearInterval(iv);
      } else {
        const days = Math.floor(sec / (3600 * 24));
        const hrs  = Math.floor((sec % (3600 * 24)) / 3600);
        const mins = Math.floor((sec % 3600) / 60);
        const secs = sec % 60;
        setTimeLeft(`${days}d ${hrs}h ${mins}m ${secs}s`);
      }
    };

    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [weekStart]);

  if (!weekStart) return null;

  return (
    <React.Fragment>
      <Col lg={12}>
        <Card style={{
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          border: 'none',
          overflow: 'hidden'
        }}>
          <CardBody style={{
            padding: '1.5rem',
            backgroundColor: '#1d222e',
            color: '#ffffff'
          }}>
            <CardTitle tag="h5" style={{
              color: '#ffffff',
              fontWeight: '600',
              marginBottom: '1rem',
              fontSize: '1.15rem'
            }}>Weekly Missions</CardTitle>

            {/* Week info + countdown */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1.25rem',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Week of {new Date(weekStart).toLocaleDateString()}
              </span>
              <span>
                <i className="ri-time-line" style={{ marginRight: '0.5rem', color: '#10b981' }}></i>
                <strong style={{ color: '#ffffff' }}>{timeLeft}</strong>
              </span>
            </div>

            {/* Mission list */}
            <ul className="list-unstyled" style={{ marginBottom: '1.5rem' }}>
              {challenges.map(ch => {
                // Use goalByKey mapping for target, fallback to extracting from key
                const target = goalByKey[ch.key] || (() => {
                  const match = ch.key.match(/(\d+)$/);
                  return match ? parseInt(match[1], 10) : 1;
                })();
                const percent = Math.min(100, (ch.progress / target) * 100);
                const remaining = Math.max(0, target - ch.progress);
                const isCompleted = ch.is_completed || remaining === 0;

                return (
                  <li key={ch.assignment_id} style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    marginBottom: '0.75rem',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.2s ease'
                  }}>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between mb-2">
                          <span style={{ 
                            fontWeight: '600', 
                            color: isCompleted ? '#10b981' : '#ffffff',
                            fontSize: '0.95rem'
                          }}>
                            {ch.name}
                            {isCompleted && (
                              <i className="ri-check-line ms-2" style={{ color: '#10b981' }}></i>
                            )}
                          </span>
                          <span style={{ 
                            backgroundColor: isCompleted ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                            color: isCompleted ? '#10b981' : 'rgba(255, 255, 255, 0.8)',
                            padding: '0.15rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            fontWeight: '500'
                          }}>
                            {ch.progress}/{target}
                          </span>
                        </div>
                        <Progress
                          value={percent}
                          style={{
                            height: "8px",
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '4px',
                            marginBottom: '0.5rem'
                          }}
                          color={isCompleted ? "success" : "info"}
                          className="mb-1"
                        />
                        <small style={{ 
                          color: isCompleted ? '#10b981' : 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.8rem'
                        }}>
                          {isCompleted
                            ? "Completed!"
                            : `${remaining} remaining`}
                        </small>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            <hr style={{ borderColor: 'rgba(255, 255, 255, 0.1)', margin: '1.5rem 0' }} />

            {/* Link out to weekly missions page */}
            <div className="text-end">
              <a 
                href="/weekly-missions" 
                style={{
                  backgroundColor: 'rgba(16, 185, 129, 0.8)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  textDecoration: 'none',
                  display: 'inline-block',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="ri-calendar-check-line me-1"></i>
                View All Weekly Missions
              </a>
            </div>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
}
